import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, CheckCircle, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const AccountSelection = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { setAccountId, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  // Fetch available accounts on component mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        console.log('🔍 Fetching available accounts...');
        const response = await axios.get('/api/auth/accounts');
        if (response.data.success && response.data.accounts.length > 0) {
          const accountsList = response.data.accounts;
          setAccounts(accountsList);
          console.log(`✅ Loaded ${accountsList.length} accounts`);
        } else {
          console.warn('⚠️ No accounts found with positive balance');
          toast.error('No accounts available');
        }
      } catch (error) {
        console.error('❌ Error fetching accounts:', error);
        toast.error('Failed to load accounts');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchAccounts();
    }
  }, [isAuthenticated]);

  const handleAccountSelect = (accountId, accountName) => {
    setSelectedAccountId(accountId);
  };

  const handleContinue = async () => {
    if (!selectedAccountId) {
      toast.error('Please select an account');
      return;
    }

    setSubmitting(true);
    const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
    const accountIndex = accounts.findIndex(acc => acc.id === selectedAccountId) + 1;
    
    // Set the account in context with index for display name
    setAccountId(selectedAccountId, selectedAccount?.name, accountIndex.toString());
    
    toast.success(`Selected ${selectedAccount?.name}`);
    
    // Navigate to dashboard
    navigate('/');
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
            <Bot className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Select Account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Choose the account you'd like to analyze
          </p>
        </div>
        
        <div className="mt-8 space-y-6">
          {accounts.length > 0 ? (
            <div className="space-y-3">
              {accounts.map((account) => (
                <div
                  key={account.id}
                  onClick={() => handleAccountSelect(account.id, account.name)}
                  className={`relative cursor-pointer rounded-lg border p-4 transition-all duration-200 hover:shadow-md ${
                    selectedAccountId === account.id
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {account.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Account ID: {account.id}
                      </p>
                    </div>
                    {selectedAccountId === account.id && (
                      <CheckCircle className="h-6 w-6 text-blue-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-8 bg-white rounded-lg border border-red-300">
              <p className="text-red-600 font-medium">No accounts available</p>
              <p className="text-sm text-gray-500 mt-2">
                Please contact your administrator to set up account access.
              </p>
            </div>
          )}

          {accounts.length > 0 && (
            <div>
              <button
                onClick={handleContinue}
                disabled={!selectedAccountId || submitting}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    Setting up...
                  </>
                ) : (
                  'Continue to Dashboard'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSelection;
